import { type DeparturesSearchRequest } from '@/features/departures/models';
import { type TripSearchRequest, WalkSpeedType } from '@/features/trips/models';
import type {
    GetDeparturesRequestDto,
    GetTripRequestDto,
    ApiResponse,
    StopPlaceDto,
    GetTripResponseDto,
    SaveTripPatternRequestDto,
    PlaceAutocompleteModelDto
} from '@/types/webapi';
import { injectable } from 'inversify';
import type { ISearchService } from '.';
import type ServiceOptions from './ServiceOptions';
import { DateType } from '@/features/common/models';
import { ApiService } from './ApiService';

@injectable()
export class SearchService extends ApiService implements ISearchService {
    async searchForTrips(
        searchRequest: TripSearchRequest & { fromPlace: PlaceAutocompleteModelDto | null; toPlace: PlaceAutocompleteModelDto | null },
        options?: ServiceOptions
    ) {
        return this.executeSearch(searchRequest, 'get', options);
    }

    async searchForMoreTrips(
        searchRequest: TripSearchRequest & { fromPlace: PlaceAutocompleteModelDto | null; toPlace: PlaceAutocompleteModelDto | null },
        options?: ServiceOptions
    ) {
        return this.executeSearch(searchRequest, 'post', options);
    }

    async executeSearch(
        searchRequest: TripSearchRequest & { fromPlace: PlaceAutocompleteModelDto | null; toPlace: PlaceAutocompleteModelDto | null },
        method: 'get' | 'post',
        options?: ServiceOptions
    ): Promise<GetTripResponseDto | null> {
        const { modes, walkSpeed: walkSpeedType, transferSlack, showTripsWithTransfers, lines } = searchRequest.filters;
        const walkSpeed = this.getWalkSpeed(walkSpeedType);

        const params: GetTripRequestDto = {
            fromPlaceId: searchRequest.fromPlace?.id,
            toPlaceId: searchRequest.toPlace?.id,
            dateTime: searchRequest.dateTimeType !== DateType.Now ? searchRequest.dateTime : undefined,
            arriveBy: searchRequest.dateTimeType === DateType.ArriveBy || undefined,
            modes: modes.length ? modes.join(',') : undefined,
            lines: lines.length ? lines.join(',') : undefined,
            walkSpeed,
            transferSlack,
            maximumTransfers: showTripsWithTransfers ? undefined : 1,
            nextPageCursors: searchRequest.nextPageCursors,
            previousPageCursors: searchRequest.previousPageCursors
        };

        const response =
            method == 'get'
                ? await this.axios.get<GetTripResponseDto>('/api/v1.0/trips', {
                      signal: options?.signal,
                      params
                  })
                : await this.axios.post<GetTripResponseDto>('/api/v1.0/trips', params, { signal: options?.signal });

        const { data, meta } = response.data;
        if (!data) return null;

        return { data, meta };
    }

    async getTripByTripPatternId(tripPatternId: string, options?: ServiceOptions): Promise<GetTripResponseDto | null> {
        const response = await this.axios.get<GetTripResponseDto>(`/api/v1.0/trips/${tripPatternId}`, { signal: options?.signal });

        return response.data;
    }

    async saveTripPattern(request: SaveTripPatternRequestDto, options?: ServiceOptions): Promise<void> {
        // get the id of the first trip pattern from the first trip pattern group
        const id = Object.values(request.tripPatternGroups)[0][0].id;

        await this.axios.post(`/api/v1.0/trips/${id}`, request, { signal: options?.signal });
    }

    async searchForDepartures(
        request: DeparturesSearchRequest & { fromPlace: PlaceAutocompleteModelDto | null; toPlace: PlaceAutocompleteModelDto | null },
        options?: ServiceOptions | undefined
    ): Promise<StopPlaceDto | null> {
        if (!request.fromPlace?.id) throw new Error('Required param missing');

        const { modes } = request.filters;

        const params: GetDeparturesRequestDto = {
            id: request.fromPlace.id,
            dateTime: request.dateTime,
            modes: modes.length ? modes.join(',') : undefined
        };

        const response = await this.axios.get<ApiResponse<StopPlaceDto>>('/api/v1.0/departures', {
            signal: options?.signal,
            params
        });

        return response.data.data;
    }

    private getWalkSpeed(type: WalkSpeedType) {
        switch (type) {
            case WalkSpeedType.Fast:
                return 2.0;
            case WalkSpeedType.Slow:
                return 0.6;
            default:
                return 1.3;
        }
    }
}
